/**
 * Settings gathered behind the spanner. The toolbar stays a row of verbs; the
 * nouns live here.
 *
 * Grouped by what the student is actually deciding: how strict the grader is,
 * how the score is engraved, how much help is on, and how it sounds.
 */

import type { JSX } from 'react';
import { CLICK_VOICES, TEMPO_MAX, TEMPO_MIN, audio } from '../audio/engine';
import { createExercise, edoOf, RULES, type Edo } from '../core';
import { useSession } from '../state/store';
import { Modal } from './Modal';

export function SettingsDialog({
  open,
  onClose,
  onConfirmNeeded,
}: {
  open: boolean;
  onClose: () => void;
  /** Asks the host to confirm before a tuning change discards written work. */
  onConfirmNeeded: (apply: () => void) => void;
}): JSX.Element {
  const exercise = useSession((s) => s.exercise);
  const settings = useSession((s) => s.settings);
  const update = useSession((s) => s.updateSettings);
  const toggleAssist = useSession((s) => s.toggleAssist);
  const loadExercise = useSession((s) => s.loadExercise);

  const applyTuning = (edo: Edo) => {
    if (edo === edoOf(exercise)) return;
    onConfirmNeeded(() => {
      loadExercise(
        createExercise(exercise.key, exercise.cfPos, settings.profile, settings.layout === 'single', edo),
        { edo },
      );
      onClose();
    });
  };

  return (
    <Modal open={open} title="Settings" onClose={onClose}>
      <fieldset className="field">
        <legend>Tuning</legend>
        <div className="segmented">
          {([12, 31] as const).map((edo) => (
            <button
              key={edo}
              type="button"
              className={edoOf(exercise) === edo ? 'segmented__item segmented__item--on' : 'segmented__item'}
              aria-pressed={edoOf(exercise) === edo}
              onClick={() => applyTuning(edo)}
            >
              {edo}-EDO
            </button>
          ))}
        </div>
        <p className="field__note">Changing tuning generates a fresh cantus firmus in the current key and voicing.</p>
      </fieldset>

      <fieldset className="field">
        <legend>Rules</legend>
        <div className="segmented">
          {RULES.map((r) => (
            <button
              key={r.id}
              type="button"
              className={settings.profile === r.id ? 'segmented__item segmented__item--on' : 'segmented__item'}
              aria-pressed={settings.profile === r.id}
              onClick={() => update({ profile: r.id })}
            >
              {r.label}
            </button>
          ))}
        </div>
        <p className="field__note">{RULES.find((r) => r.id === settings.profile)?.blurb}</p>
      </fieldset>

      <fieldset className="field">
        <legend>Engraving</legend>
        <div className="segmented">
          {(
            [
              ['single', 'One staff'],
              ['grand', 'Grand staff'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={settings.layout === value ? 'segmented__item segmented__item--on' : 'segmented__item'}
              aria-pressed={settings.layout === value}
              onClick={() => update({ layout: value })}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="field__note">
          One staff puts both voices together the way the source videos do; the grand staff gives each voice its own
          clef.
        </p>
      </fieldset>

      <fieldset className="field">
        <legend>Theme</legend>
        <div className="segmented">
          {(
            [
              ['system', 'System'],
              ['light', 'Light'],
              ['dark', 'Dark'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={settings.theme === value ? 'segmented__item segmented__item--on' : 'segmented__item'}
              aria-pressed={settings.theme === value}
              onClick={() => update({ theme: value })}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="field">
        <legend>Assists</legend>
        <Toggle
          label="Interval labels"
          note="The interval number under every measure, and the motion ribbons between them."
          checked={settings.assists.labels}
          onChange={() => toggleAssist('labels')}
        />
        <Toggle
          label="Allowed notes"
          note="Press or hover a measure to see every note the rules still permit there — and be warned when a measure has run out."
          checked={settings.assists.allowed}
          onChange={() => toggleAssist('allowed')}
        />
        <p className="field__note">
          The grade marks which dimensions an assist helped with. Switch both off to earn a cold score.
        </p>
      </fieldset>

      <fieldset className="field">
        <legend>Sound</legend>
        <label className="slider">
          <span>
            Tempo <b>{settings.tempo}</b> whole notes / min
          </span>
          <input
            type="range"
            min={TEMPO_MIN}
            max={TEMPO_MAX}
            value={settings.tempo}
            onChange={(e) => update({ tempo: Number(e.target.value) })}
          />
        </label>

        <label className="slider">
          <span>Volume</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(settings.volume * 100)}
            onChange={(e) => update({ volume: Number(e.target.value) / 100 })}
          />
        </label>

        <label className="select">
          <span>Playback voices</span>
          <select
            value={settings.playbackVoices}
            onChange={(e) => update({ playbackVoices: e.target.value as typeof settings.playbackVoices })}
          >
            <option value="both">Both voices</option>
            <option value="cf">Cantus firmus only</option>
            <option value="cp">My line only</option>
          </select>
        </label>

        <Toggle
          label="Sound the interval as I write"
          note="Each note you place is heard against the cantus firmus straight away."
          checked={settings.auditionOnPlace}
          onChange={() => update({ auditionOnPlace: !settings.auditionOnPlace })}
        />
      </fieldset>

      <fieldset className="field">
        <legend>Metronome</legend>
        <Toggle
          label="Click during playback"
          note="Off by default. The tap-tempo button still sounds when you tap it."
          checked={settings.metronomeDuringPlayback}
          onChange={() => update({ metronomeDuringPlayback: !settings.metronomeDuringPlayback })}
        />
        <label className="select">
          <span>Click voice</span>
          <select
            value={settings.clickVoice}
            onChange={(e) => {
              update({ clickVoice: e.target.value as typeof settings.clickVoice });
              audio.click();
            }}
          >
            {CLICK_VOICES.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
        <label className="slider">
          <span>Click volume</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(settings.clickVolume * 100)}
            onChange={(e) => update({ clickVolume: Number(e.target.value) / 100 })}
            onPointerUp={() => audio.click()}
          />
        </label>
      </fieldset>

      <fieldset className="field">
        <legend>Safety</legend>
        <Toggle
          label="Ask before discarding work"
          note="Confirms a new exercise when your line is not empty, and confirms grading."
          checked={settings.confirmDestructive}
          onChange={() => update({ confirmDestructive: !settings.confirmDestructive })}
        />
      </fieldset>
    </Modal>
  );
}

/** A labelled switch. Rendered from a real checkbox so it behaves like one. */
function Toggle({
  label,
  note,
  checked,
  onChange,
}: {
  label: string;
  note?: string;
  checked: boolean;
  onChange: () => void;
}): JSX.Element {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="toggle__switch" aria-hidden="true" />
      <span className="toggle__text">
        {label}
        {note && <em>{note}</em>}
      </span>
    </label>
  );
}
